import { io, Socket } from 'socket.io-client';
import { Message } from '../types';

class SocketClient {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isConnecting = false; // 接続中フラグを追加
  private connectionFailed = false; // 接続失敗フラグを追加

  constructor() {}

  private connect(): void {
    // 既に接続中、接続済み、または接続失敗している場合はスキップ
    if (this.isConnecting || (this.socket && this.isConnected) || this.connectionFailed) {
      return;
    }
    
    this.isConnecting = true;
    
    try {
      const serverUrl = window.location.origin; // 同一オリジン
      // 既存接続があれば再利用
      const existing = (window as any).CSCHAT_SOCKET as Socket | undefined;
      if (existing && existing.connected) {
        this.socket = existing;
        this.isConnected = true;
        this.isConnecting = false;
      } else {
        this.socket = io(serverUrl, {
          path: '/socket.io/',
          transports: ['websocket', 'polling'], // websocketを優先し、失敗時はpollingにフォールバック
          withCredentials: true,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
        });
        (window as any).CSCHAT_SOCKET = this.socket;
      }
      this.setupEventListeners();
    } catch (error) {
      console.error('🔌 Socket.IO初期化エラー:', error);
      this.isConnecting = false;
      this.connectionFailed = true;
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('🔌 Socket.IO接続成功');
      this.isConnected = true;
      this.isConnecting = false;
      this.connectionFailed = false; // 接続成功したので失敗フラグをリセット
      this.reconnectAttempts = 0;
      try { (window as any).CSCHAT_SOCKET_ID = this.socket!.id; } catch {}
      window.dispatchEvent(new Event('rt:connected'));
      
      // 認証を少し遅延させる
      setTimeout(() => {
        this.authenticate();
      }, 500);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket.IO接続切断:', reason);
      this.isConnected = false;
      window.dispatchEvent(new Event('rt:disconnected'));
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔌 Socket.IO接続エラー:', error);
      this.reconnectAttempts++;
      this.isConnecting = false; // 接続試行失敗
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('🔌 Socket.IO最大再接続試行回数に達しました');
        // 接続を完全に停止
        this.socket?.disconnect();
        this.socket = null;
        this.isConnected = false;
        this.connectionFailed = true; // 接続失敗フラグを立てる
      }
    });

    this.socket.on('error', (error) => {
      console.error('🔌 Socket.IOエラー:', error);
    });

    // 認証成功/失敗イベント
    this.socket.on('authenticated', (data) => {
      console.log('✅ Socket.IO認証成功:', data);
    });

    this.socket.on('authentication-error', (error) => {
      // エンドユーザーは認証不要のため、エラーではなく警告として扱う
      console.warn('⚠️ Socket.IO認証失敗（エンドユーザーの場合は正常）:', error);
    });

    // ルーム参加/退出イベント（サーバのイベント名に合わせる）
    this.socket.on('roomJoined', (data: any) => {
      try { (window as any).CSCHAT_SOCKET_ID = data?.socketId || this.socket?.id; } catch {}
      try { window.dispatchEvent(new CustomEvent('rt:roomJoined', { detail: data })); } catch {}
      console.log('🏠 ルーム参加成功:', data);
    });

    this.socket.on('roomLockUpdate', (payload: any) => {
      try { window.dispatchEvent(new CustomEvent('rt:roomLockUpdate', { detail: payload })); } catch {}
      console.log('🔒 ルームロック更新:', payload);
    });

    this.socket.on('roomLocked', (payload: any) => {
      try { window.dispatchEvent(new CustomEvent('rt:roomLocked', { detail: payload })); } catch {}
      console.warn('🔒 ルームはロック中です:', payload);
    });

    this.socket.on('room:forceKicked', (payload: any) => {
      try { window.dispatchEvent(new CustomEvent('rt:forceKicked', { detail: payload })); } catch {}
      console.warn('⚡ 強制退室通知:', payload);
    });

    // 新着メッセージ（ルーム）をグローバルイベントでも配信
    this.socket.on('newMessage', (message) => {
      console.log('📡 Socket.IO newMessage イベント受信:', message);
      try {
        window.dispatchEvent(new CustomEvent('rt:newMessage', { detail: message }));
        console.log('✅ rt:newMessage イベント配信完了');
      } catch (err) {
        console.error('❌ rt:newMessage イベント配信エラー:', err);
      }
    });

    // 全体通知用（統計/バッジ向け）
    this.socket.on('globalNewMessage', (message) => {
      try {
        window.dispatchEvent(new CustomEvent('rt:newMessage', { detail: message }));
      } catch {}
    });

    // タイピング状態
    this.socket.on('userTyping', (data) => {
      try {
        window.dispatchEvent(new CustomEvent('rt:userTyping', { detail: data }));
      } catch {}
    });
  }

  private authenticate(): void {
    if (!this.socket || !this.isConnected) return;

    // 管理者トークンまたはエンドユーザートークンを取得
    const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('admin_token');
    const endUserToken = localStorage.getItem('endUserToken');
    
    const token = adminToken || endUserToken;
    
    if (token) {
      this.socket.emit('authenticate', token);
    }
  }

  // 接続開始
  connect_socket(): void {
    this.connect();
  }

  // 自動ルーム参加（認証不要）
  autoJoinRoom(roomId: string): void {
    if (this.socket) {
      const connectAndJoin = () => {
        if (this.isConnected) {
          console.log('🏠 自動ルーム参加:', roomId);
          this.socket!.emit('joinRoom', roomId);
        } else {
          // 接続完了まで少し待つ
          setTimeout(connectAndJoin, 500);
        }
      };
      connectAndJoin();
    }
  }

  // 接続終了
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

  // チャットルームに参加（サーバーのイベント名）
  joinRoom(roomId: string): void {
    if (this.socket && this.isConnected) {
      console.log('🏠 チャットルーム参加:', roomId);
      this.socket.emit('joinRoom', roomId);
    } else {
      console.warn('⚠️ Socket.IO未接続のためルーム参加をスキップ');
    }
  }

  // チャットルームから退出（サーバーのイベント名）
  leaveRoom(roomId: string): void {
    if (this.socket && this.isConnected) {
      console.log('🚪 チャットルーム退出:', roomId);
      this.socket.emit('leaveRoom', roomId);
    }
  }

  // メッセージ送信
  sendMessage(roomId: string, content: string, senderType: 'admin' | 'end_user' = 'admin'): void {
    if (this.socket && this.isConnected) {
      console.log('📤 メッセージ送信:', { roomId, content, senderType });
      this.socket.emit('sendMessage', {
        roomId,
        content,
        sender_type: senderType,
        message_type: 'text'
      });
    }
  }

  // タイピング開始
  startTyping(roomId: string): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('startTyping', { roomId });
    }
  }

  // タイピング終了
  stopTyping(roomId: string): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('stopTyping', { roomId });
    }
  }

  // ステータス更新
  updateStatus(status: 'online' | 'away' | 'busy'): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('update-status', status);
    }
  }

  // イベント購読API（既存ページ互換）
  onNewMessage(callback: (message: Message) => void): void {
    if (this.socket) {
      this.socket.on('newMessage', callback);
    }
  }

  offNewMessage(callback?: (message: Message) => void): void {
    if (this.socket) {
      this.socket.off('newMessage', callback as any);
    }
  }

  onMessageRead(callback: (data: { messageId: string; roomId: string; readBy: string }) => void): void {
    if (this.socket) {
      this.socket.on('messageRead', callback as any);
    }
  }

  offMessageRead(callback?: (data: { messageId: string; roomId: string; readBy: string }) => void): void {
    if (this.socket) {
      this.socket.off('messageRead', callback as any);
    }
  }

  onUserTyping(callback: (data: { userId: string; username: string; roomId: string; isTyping: boolean }) => void): void {
    if (this.socket) {
      this.socket.on('userTyping', callback as any);
    }
  }

  offUserTyping(callback?: (data: { userId: string; username: string; roomId: string; isTyping: boolean }) => void): void {
    if (this.socket) {
      this.socket.off('userTyping', callback as any);
    }
  }

  // 接続状態確認
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }
}

// エンドユーザー用Socket.IOクライアント
class EndUserSocketClient extends SocketClient {
  // エンドユーザー用のメッセージ送信
  sendMessage(roomId: string, content: string): void {
    super.sendMessage(roomId, content, 'end_user');
  }

  // 互換API
  onUserJoined(callback: (data: any) => void): void {
    const sock = (this as any).socket as Socket | null;
    if (sock) sock.on('userJoined', callback as any);
  }
  onUserLeft(callback: (data: any) => void): void {
    const sock = (this as any).socket as Socket | null;
    if (sock) sock.on('userLeft', callback as any);
  }
  onError(callback: (error: any) => void): void {
    const sock = (this as any).socket as Socket | null;
    if (sock) sock.on('error', callback as any);
  }
  offUserJoined(callback?: (data: any) => void): void {
    const sock = (this as any).socket as Socket | null;
    if (sock) sock.off('userJoined', callback as any);
  }
  offUserLeft(callback?: (data: any) => void): void {
    const sock = (this as any).socket as Socket | null;
    if (sock) sock.off('userLeft', callback as any);
  }
  offError(callback?: (error: any) => void): void {
    const sock = (this as any).socket as Socket | null;
    if (sock) sock.off('error', callback as any);
  }
}

// シングルトンインスタンス
const socketClient = new SocketClient();
const endUserSocketClient = new EndUserSocketClient();

export default socketClient;
export { endUserSocketClient }; 